import { useState, useEffect, useRef } from "react";
import { Mountain, ShieldCheck, Loader2, ArrowRight, ArrowLeft, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { verifyAdminCode, adminLogin } from "@/lib/api";

export function AdminAuthModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState("code");
  const [code, setCode] = useState(["", "", "", ""]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [lock, setLock] = useState(0);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    if (open) {
      setStep("code"); setCode(["", "", "", ""]); setEmail(""); setPassword(""); setBusy(false);
      setTimeout(() => refs[0].current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (lock <= 0) return;
    const t = setInterval(() => setLock((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [lock]);

  const handleErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 429) {
      const ra = (d && d.retry_after) || 60;
      setLock(ra);
      toast.error((d && d.message) || `Too many attempts. Try again in ${ra}s.`);
    } else {
      toast.error((d && (d.message || d)) || fallback);
    }
  };

  const setDigit = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setCode((prev) => { const n = [...prev]; n[i] = d; return n; });
    if (d && i < 3) refs[i + 1].current?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) refs[i - 1].current?.focus();
  };
  const onPaste = (e) => {
    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (t) { e.preventDefault(); setCode([t[0] || "", t[1] || "", t[2] || "", t[3] || ""]); refs[Math.min(t.length, 3)].current?.focus(); }
  };

  const submitCode = async (e) => {
    e?.preventDefault?.();
    if (lock > 0) return;
    const c = code.join("");
    if (c.length !== 4) return toast.error("Enter the 4-digit access code");
    setBusy(true);
    try { await verifyAdminCode(c); setStep("creds"); setTimeout(() => document.querySelector('[data-testid="admin-email"]')?.focus(), 80); }
    catch (err) { handleErr(err, "Invalid access code"); setCode(["", "", "", ""]); refs[0].current?.focus(); }
    finally { setBusy(false); }
  };

  const submitCreds = async (e) => {
    e?.preventDefault?.();
    if (lock > 0) return;
    if (!email.trim() || !password) return toast.error("Enter your email and password");
    setBusy(true);
    try { await adminLogin(email.trim(), password); onSuccess(); }
    catch (err) { handleErr(err, "Invalid email or password"); }
    finally { setBusy(false); }
  };

  const locked = lock > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-gate"
        className="max-w-sm overflow-hidden rounded-2xl border-0 p-0 [&>button]:right-5 [&>button]:top-5 [&>button]:text-white/60 [&>button]:hover:text-white [&>button]:opacity-100"
      >
        <DialogTitle className="sr-only">Authorized Access</DialogTitle>
        <DialogDescription className="sr-only">Enter your admin access code, then your email and password.</DialogDescription>

        <div className="bg-gradient-to-b from-[#163323] to-[#0d2116] px-8 pb-8 pt-10 text-center">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-[#c8a452]/40">
            <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-14 w-14 object-contain" />
          </span>
          <h2 className="mt-5 font-serif text-3xl font-bold text-white">Authorized Access</h2>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-white/60">
            Data Earth Surveying Command Center — restricted to authorized administrators.
          </p>
        </div>

        <div className="bg-white px-8 pb-8 pt-7">
          <div className="mx-auto mb-6 flex w-fit items-center gap-2 rounded-full bg-emerald-50 px-4 py-1.5 text-sm font-medium text-emerald-700 ring-1 ring-emerald-100">
            <ShieldCheck className="h-4 w-4" />
            {step === "code" ? "Secure Admin Portal" : "Step 2 — Sign in"}
          </div>

          {locked && (
            <div data-testid="admin-lockout" className="mb-5 flex items-center gap-2 rounded-lg bg-[#fef2f2] px-4 py-3 text-sm text-[#b91c1c] ring-1 ring-[#fecaca]">
              <ShieldAlert className="h-4 w-4 shrink-0" />
              Too many failed attempts. Try again in <strong>{lock}s</strong>.
            </div>
          )}

          {step === "code" ? (
            <form onSubmit={submitCode}>
              <p className="mb-4 text-center text-base text-[#3d4742]">Enter your 4-digit access code.</p>
              <div className="flex justify-center gap-3" onPaste={onPaste}>
                {code.map((d, i) => (
                  <input
                    key={i} ref={refs[i]} data-testid={`admin-code-${i}`}
                    value={d} inputMode="numeric" maxLength={1} disabled={locked}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => onKeyDown(i, e)}
                    className="h-16 w-14 rounded-xl border border-[#d2dad5] bg-white text-center text-3xl font-bold text-[#163323] outline-none transition-all focus:border-[#c8a452] focus:ring-2 focus:ring-[#c8a452]/40 disabled:opacity-50"
                  />
                ))}
              </div>
              <Button type="submit" data-testid="admin-code-submit" disabled={busy || locked}
                className="mt-7 h-13 w-full rounded-full bg-[#163323] py-3 text-base font-semibold text-white hover:bg-[#0d2116] disabled:opacity-60">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : locked ? `Locked (${lock}s)` : <>Verify Access <ArrowRight className="ml-2 h-5 w-5" /></>}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitCreds} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#1a221e]">Access Email</label>
                <Input data-testid="admin-email" type="email" value={email} disabled={locked}
                  onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email"
                  className="h-12 rounded-lg border-[#d2dad5] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-[#1a221e]">Password</label>
                <Input data-testid="admin-password" type="password" value={password} disabled={locked}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                  className="h-12 rounded-lg border-[#d2dad5] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0" />
              </div>
              <Button type="submit" data-testid="admin-login-submit" disabled={busy || locked}
                className="h-13 w-full rounded-full bg-[#c8a452] py-3 text-base font-semibold text-[#163323] hover:bg-[#b38f3f] disabled:opacity-60">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : locked ? `Locked (${lock}s)` : <>Verify Access <ArrowRight className="ml-2 h-5 w-5" /></>}
              </Button>
              <button type="button" onClick={() => setStep("code")}
                className="mx-auto flex items-center gap-1.5 text-xs text-[#5a6660] transition-colors hover:text-[#163323]">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to access code
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
